import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { SkeletonBlurb } from 'components/Containers';
import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import SummarizationActionsMenu from 'components/summarization/SummarizationActionsMenu';
import { capitalizeGender } from 'lib/util';
import Summarization, { SummarizationOptions } from 'models/Summarization';
import { summarizationQueryKeys } from 'services/api/summarizations';
import { queryClient } from 'services/http';
import { useMobileNavigationActions } from 'stores/mobileNavigationStore';
import colors from 'styles/theme/colors';
import { BodySmall, H3 } from 'styles/typography';
import HamburgerMenuIcon from 'svg/HamburgerMenuIcon';
import SavedIcon from 'svg/SavedIcon';

interface Props {
  summarization: Summarization;
}

export default function Header({ summarization }: Props) {
  const { setNavigationOpen } = useMobileNavigationActions();
  const navigate = useNavigate();

  const { hasBeenViewed, lastSeenAt, patientDemographicsJob, patient, title } = summarization;
  const { name, dateOfBirth, gender } = patient ?? {};

  const hasBeenViewedSincePatientDemographicsPopulated =
    patientDemographicsJob?.isCompleted &&
    lastSeenAt &&
    patientDemographicsJob.updatedAt &&
    lastSeenAt > new Date(patientDemographicsJob.updatedAt);

  const showTitleAnimation = !hasBeenViewed;

  const [showSavedToHistory, setShowSavedToHistory] = useState(hasBeenViewed);
  const [showPatientName, setShowPatientName] = useState(hasBeenViewedSincePatientDemographicsPopulated);
  const [showPatientDOB, setShowPatientDOB] = useState(hasBeenViewedSincePatientDemographicsPopulated);
  const [showPatientGender, setShowPatientGender] = useState(hasBeenViewedSincePatientDemographicsPopulated);
  const [showPatientDemographicsAnimation, setShowPatientDemographicsAnimation] = useState(false);

  useEffect(() => {
    if (!hasBeenViewed) {
      const timer = setTimeout(() => {
        setShowSavedToHistory(true);

        queryClient.setQueryData(
          summarizationQueryKeys.show({ id: summarization.id }),
          new Summarization({ ...summarization, hasBeenViewed: true } as SummarizationOptions)
        );
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [hasBeenViewed, summarization]);

  useEffect(() => {
    if (name && !showPatientName) {
      const timer = setTimeout(() => {
        setShowPatientName(true);
        setShowPatientDemographicsAnimation(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [name, showPatientName]);

  useEffect(() => {
    if (dateOfBirth && !showPatientDOB) {
      let timeout = 1000;
      if (name) timeout += 1000;

      const timer = setTimeout(() => {
        setShowPatientDOB(true);
        setShowPatientDemographicsAnimation(true);
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [dateOfBirth, showPatientDOB, name]);

  useEffect(() => {
    if (gender && !showPatientGender) {
      let timeout = 1000;
      if (name) timeout += 1000;
      if (dateOfBirth) timeout += 750;

      const timer = setTimeout(() => {
        setShowPatientGender(true);
        setShowPatientDemographicsAnimation(true);
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [gender, showPatientGender, dateOfBirth, name]);

  return (
    <Page.Header>
      <HeaderGroup>
        <HeaderInfoRow>
          <HeaderInfo>
            <FlexGroup style={{ gap: 16 }}>
              <MobileOnly>
                <Button variant='icon' onClick={() => setNavigationOpen(true)} data-cy='hamburgerIconButton'>
                  <HamburgerMenuIcon />
                </Button>
              </MobileOnly>
              <Title showAnimation={showTitleAnimation}>{title}</Title>
              <FlexGroup style={{ gap: 8, visibility: showSavedToHistory ? 'visible' : 'hidden' }}>
                <div>
                  <SavedIcon />
                </div>
                <BodySmall>
                  Saved to <Link onClick={() => navigate('/summarizations')}>History</Link>
                </BodySmall>
              </FlexGroup>
            </FlexGroup>
          </HeaderInfo>
          <SummarizationActionsMenu summarization={summarization} />
        </HeaderInfoRow>
        {name &&
          (showPatientName ? (
            <PatientName showAnimation={showPatientDemographicsAnimation}>{name}</PatientName>
          ) : (
            <SkeletonBlank $width={120} $height={20} />
          ))}
        {(dateOfBirth || gender) && (
          <ChipContainer>
            {!(showPatientDOB || showPatientGender) && <SkeletonBlank $width={200} $height={30} />}
            {dateOfBirth && showPatientDOB && (
              <PatientDemographicsChip showAnimation={showPatientDemographicsAnimation}>
                DOB: {dateOfBirth}
              </PatientDemographicsChip>
            )}
            {gender && showPatientGender && (
              <PatientDemographicsChip showAnimation={showPatientDemographicsAnimation}>
                Gender: {capitalizeGender(gender)}
              </PatientDemographicsChip>
            )}
          </ChipContainer>
        )}
      </HeaderGroup>
    </Page.Header>
  );
}

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 24px 40px 24px 40px;
`;

const HeaderInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  width: 100%;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FlexGroup = styled.div`
  display: flex;
  align-items: center;
`;

const Link = styled.a`
  color: var(--primary-blue);
  cursor: pointer;
  text-decoration: underline;
`;

const ChipContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const Chip = styled.div<{ backgroundColor?: string; color?: string }>`
  background-color: ${colors.black5};
  color: ${colors.black100};
  border-radius: 3px;
  padding: 8px;
  font-size: 12px;
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  cursor: default;
`;

const FadeInAnimation = `
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Title = styled(H3)<{ showAnimation?: boolean }>`
  overflow: hidden;
  white-space: nowrap;

  ${({ showAnimation }) =>
    showAnimation &&
    `
    animation:
      typewriter 0.75s steps(1000),
      0.75s step-end infinite;

    @keyframes typewriter {
      from {
        max-width: 0;
      }
      to {
        max-width: 100%;
      }
    }
  `}
`;

const PatientName = styled(BodySmall)<{ showAnimation?: boolean }>`
  overflow: hidden;
  white-space: nowrap;

  ${({ showAnimation }) => showAnimation && FadeInAnimation}
`;

const PatientDemographicsChip = styled(Chip)<{ showAnimation?: boolean }>`
  ${({ showAnimation }) => showAnimation && FadeInAnimation}
`;

const SkeletonBlank = styled(SkeletonBlurb)<{ $width: number; $height: number; $spacing?: number }>`
  border-radius: ${({ $height }) => $height + 'px'};
  height: ${({ $height }) => $height + 'px'};
  width: ${({ $width }) => $width + 'px'};
  margin-bottom: ${({ $spacing }) => ($spacing ?? 0) + 'px'};
`;
